import { RecommendationsUI } from "./RecommendationsUI";
import { StandardProps } from "../componentMap";
import { useVariant } from "@/hooks/useVariant/useVariant";
import { ensureIsActiveVariant } from "@/utils/product/ensureIsActive";
import { getNotifiableEventType } from "@/components/__shared__/NotAvailable/NotAvailable";
import { hasAvailableVariantsMatchingColour } from "@/utils/product/hasAvailableVariantsMatchingColour";

function isValidRecommendationsType(value = "") {
  return value === "small" || value === "small-manual-recs";
}

export function Recommendations({
  product,
  configProps,
  flags,
}: Pick<StandardProps, "product" | "configProps" | "flags">) {
  const { variant, isSelected } = useVariant(product);
  const activeVariant = ensureIsActiveVariant(variant);

  const placementId = configProps?.placementId
    ? String(configProps.placementId)
    : "";
  const recommendationsType = String(configProps?.type);
  const type = isValidRecommendationsType(recommendationsType)
    ? recommendationsType
    : undefined;
  const maxProductsPerPage = configProps?.maxProductsPerPage
    ? String(configProps.maxProductsPerPage)
    : "5";
  const allowRefresh = configProps?.allowRefresh ? "true" : "false";

  const hideForOOSAndNotifiable = configProps?.hideForOOSAndNotifiable;
  const hideForAvailable = configProps?.hideForAvailable;

  const isResponsiveStylesEnabled =
    flags.isResponsiveStylesEnabled.value === true;
  const isNoOuterSpacingEnabled =
    flags.recommendationsNoOuterSpacing.value === true;

  const isCtaPremiumDesign = flags?.ctaPremiumDesign?.value === true;
  const isPremiumColourSelector = flags.premiumColourSelector.value === true;

  const notifiableEventType =
    activeVariant &&
    activeVariant.customerNotifiableEvents &&
    getNotifiableEventType(activeVariant.customerNotifiableEvents);

  const isSelectedVariantOutOfStock =
    isSelected &&
    !variant.availability?.availableToOrder &&
    variant.availability?.unavailableReason === "outOfStock";

  let isEntireVariantGroupOutOfStock = false;

  if (product.variantGroups) {
    const currentVariantGroup = product.variantGroups?.find((variantGroup) =>
      variantGroup.variants.some(({ id }) => {
        return id === variant.id;
      }),
    );

    const currentVariantGroupVariantIDs = currentVariantGroup?.variants.map(
      (variant) => variant.id,
    );
    const currentVariantGroupVariants = product.variants.filter((variant) =>
      currentVariantGroupVariantIDs?.includes(variant.id),
    );

    isEntireVariantGroupOutOfStock = currentVariantGroupVariants.every(
      (variant) =>
        "availability" in variant &&
        !variant.availability.availableToOrder &&
        variant.availability.unavailableReason === "outOfStock",
    );
  }

  const isNotifiable = isSelected && !!notifiableEventType;

  const hideRecsPanel = hideForOOSAndNotifiable
    ? isSelectedVariantOutOfStock ||
      isEntireVariantGroupOutOfStock ||
      isNotifiable
    : false;

  if (
    hideForAvailable &&
    ((isSelected && variant.availability?.availableToOrder === true) ||
      (!isSelected &&
        hasAvailableVariantsMatchingColour(
          product,
          variant.differentiators?.colour,
        )))
  ) {
    return null;
  }

  if (hideRecsPanel) {
    return null;
  }

  return (
    <RecommendationsUI
      placement={placementId}
      productId={product.aliases.pimProductId}
      skuId={variant.aliases.skuId}
      maxProductsPerPage={maxProductsPerPage}
      allowRefresh={allowRefresh}
      type={type}
      isResponsiveStylesEnabled={isResponsiveStylesEnabled}
      isNoOuterSpacingEnabled={isNoOuterSpacingEnabled}
      isCtaPremiumDesign={isCtaPremiumDesign}
      isPremiumColourSelector={isPremiumColourSelector}
    />
  );
}
